import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Users, FileText, Calendar, Globe, Shield, Clock, Phone, TrendingUp, Building, DollarSign, Target, Briefcase, PieChart } from 'lucide-react';

const InvestorEntrepreneurVisas = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    transition: { duration: 0.6 },
    viewport: { once: true }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <motion.section 
        className="bg-custom-pattern py-16 px-6 text-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-6xl font-inter font-bold md:font-black text-acBlack leading-tight mb-6">
            Investor and Entrepreneur Visas
          </h1>
          <p className="text-lg text-gray-600 mb-8 max-w-3xl mx-auto">
            Evidence the source of funds and the viability of your plan, then execute with confidence.
          </p>
          
          <div className="bg-white rounded-lg shadow-lg p-8 text-left max-w-3xl mx-auto">
            <h2 className="text-2xl font-semibold text-acBlack mb-4">Snapshot</h2>
            <p className="text-gray-600 leading-relaxed">
              We support investors and founders who want to relocate by investing in, starting, or acquiring a business. Our work focuses on a defensible source of funds trail, a credible business plan, and filings that match the exact legal criteria in your destination. We regularly handle the United Kingdom, United States, Canada, Germany and wider EU, and the UAE. Response time is 24 to 72 hours. London covers EMEA. New York runs an APAC desk for evening ET overlap.
            </p>
          </div>
        </div>
      </motion.section>

      {/* Who this is for Section */}
      <motion.section className="py-16 px-6" {...fadeInUp}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack text-center mb-12">
            Who this is for
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-8">
            <div className="text-center p-6 bg-blue-50 rounded-lg">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                <DollarSign className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">High Net Worth Individuals</h3>
              <p className="text-gray-600 text-sm">High net worth and upper-middle income individuals planning to invest or acquire a business abroad</p>
            </div>
            <div className="text-center p-6 bg-blue-50 rounded-lg">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Founders & Entrepreneurs</h3>
              <p className="text-gray-600 text-sm">Founders, co-founders, and key early employees relocating to run or scale a startup</p>
            </div>
            <div className="text-center p-6 bg-blue-50 rounded-lg">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                <Briefcase className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Family Office Principals</h3>
              <p className="text-gray-600 text-sm">Family office principals, angel and VC backed founders, and search-fund operators</p>
            </div>
            <div className="text-center p-6 bg-blue-50 rounded-lg">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                <Building className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Business Buyers</h3>
              <p className="text-gray-600 text-sm">Business buyers pursuing acquisition of an operating company</p>
            </div>
            <div className="text-center p-6 bg-blue-50 rounded-lg">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Previous Refusals</h3>
              <p className="text-gray-600 text-sm">Applicants with a past refusal who need a stronger documentary strategy</p>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Outcomes Section */}
      <motion.section className="py-16 px-6 bg-gray-50" {...fadeInUp}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack text-center mb-12">
            Outcomes you can expect
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Target className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Written Strategy</h3>
              <p className="text-gray-600 text-sm">Most suitable route, required investment amount, ownership and control rules, and realistic timeline</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Business Plan</h3>
              <p className="text-gray-600 text-sm">Immigration-standard plan matching market data, staffing plans, and financial projections</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <PieChart className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Source of Funds Trail</h3>
              <p className="text-gray-600 text-sm">Fully evidenced source of funds and wealth trail with translations and legalisations</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Building className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Corporate Formation</h3>
              <p className="text-gray-600 text-sm">Corporate formation and banking coordination where local rules allow</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Complete Application</h3>
              <p className="text-gray-600 text-sm">Quality-checked application file, booked appointments, and interview preparation</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Calendar className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Renewals Plan</h3>
              <p className="text-gray-600 text-sm">Clear renewals and permanent residence plan if the route allows it</p>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Eligibility Basics Section */}
      <motion.section className="py-16 px-6" {...fadeInUp}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack text-center mb-12">
            Eligibility basics
          </h2>
          <p className="text-lg text-gray-600 text-center mb-8">
            Rules vary by country. The following themes are common.
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="flex items-start">
              <CheckCircle className="w-6 h-6 text-blue-500 mr-4 mt-1 flex-shrink-0" />
              <div>
                <h3 className="text-lg font-inter font-semibold text-acBlack mb-2">Investment Requirements</h3>
                <p className="text-gray-600">Minimum investment amount and permitted investment types</p>
              </div>
            </div>
            <div className="flex items-start">
              <CheckCircle className="w-6 h-6 text-blue-500 mr-4 mt-1 flex-shrink-0" />
              <div>
                <h3 className="text-lg font-inter font-semibold text-acBlack mb-2">Ownership & Control</h3>
                <p className="text-gray-600">Ownership and control thresholds for the applicant</p>
              </div>
            </div>
            <div className="flex items-start">
              <CheckCircle className="w-6 h-6 text-blue-500 mr-4 mt-1 flex-shrink-0" />
              <div>
                <h3 className="text-lg font-inter font-semibold text-acBlack mb-2">Active Management</h3>
                <p className="text-gray-600">Active management or founder involvement rather than passive investment only</p>
              </div>
            </div>
            <div className="flex items-start">
              <CheckCircle className="w-6 h-6 text-blue-500 mr-4 mt-1 flex-shrink-0" />
              <div>
                <h3 className="text-lg font-inter font-semibold text-acBlack mb-2">Economic Benefit</h3>
                <p className="text-gray-600">Job creation or economic benefit requirements where applicable</p>
              </div>
            </div>
            <div className="flex items-start">
              <CheckCircle className="w-6 h-6 text-blue-500 mr-4 mt-1 flex-shrink-0" />
              <div>
                <h3 className="text-lg font-inter font-semibold text-acBlack mb-2">Source of Funds</h3>
                <p className="text-gray-600">Lawful and traceable source of funds and source of wealth</p>
              </div>
            </div>
            <div className="flex items-start">
              <CheckCircle className="w-6 h-6 text-blue-500 mr-4 mt-1 flex-shrink-0" />
              <div>
                <h3 className="text-lg font-inter font-semibold text-acBlack mb-2">Genuine Business Activity</h3>
                <p className="text-gray-600">Genuine business activity with premises, customers, or contracts as appropriate</p>
              </div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Typical Routes Section */}
      <motion.section className="py-16 px-6 bg-gray-50" {...fadeInUp}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack text-center mb-12">
            Typical routes we cover at a high level
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-xl font-inter font-bold text-acBlack mb-4">United Kingdom</h3>
              <p className="text-gray-600 text-sm">Founder and entrepreneur focused routes that require endorsement or innovation credentials, and routes for establishing and expanding a presence where criteria are met</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-xl font-inter font-bold text-acBlack mb-4">United States</h3>
              <p className="text-gray-600 text-sm">Treaty investor for eligible nationalities, immigrant investor for qualifying projects, and founder-led categories where the profile fits</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-xl font-inter font-bold text-acBlack mb-4">Canada</h3>
              <p className="text-gray-600 text-sm">Federal or provincial entrepreneur and startup programs, and work permit pathways that lead to permanent residence where the rules allow</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-xl font-inter font-bold text-acBlack mb-4">Germany and wider EU</h3>
              <p className="text-gray-600 text-sm">National entrepreneur and self-employment routes, EU Blue Card alternatives for founder-employees, and limited residency by investment programs in specific countries</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-xl font-inter font-bold text-acBlack mb-4">UAE</h3>
              <p className="text-gray-600 text-sm">Investor and entrepreneur residency, free-zone company setups, and long-term categories for qualifying investors</p>
            </div>
          </div>
          <p className="text-center text-gray-600 mt-8">
            Because investor and founder rules change frequently, we confirm the current criteria during intake for your exact destination and nationality.
          </p>
        </div>
      </motion.section>

      {/* What you get from us Section */}
      <motion.section className="py-16 px-6" {...fadeInUp}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack text-center mb-12">
            What you get from us
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Target className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-xl font-inter font-semibold text-acBlack mb-3">Strategy and Planning</h3>
              <p className="text-gray-600">Route comparison with investment amounts, control thresholds, and renewal pathways</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-xl font-inter font-semibold text-acBlack mb-3">Business Plan & Market Evidence</h3>
              <p className="text-gray-600">Immigration-standard plan with projections, staffing, and market references</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <PieChart className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-xl font-inter font-semibold text-acBlack mb-3">Source of Funds & Wealth</h3>
              <p className="text-gray-600">Evidence tree for each funding path with audit-style checklist and translations</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Building className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-xl font-inter font-semibold text-acBlack mb-3">Corporate & Banking</h3>
              <p className="text-gray-600">Company formation, compliance coordination, and bank account guidance</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-xl font-inter font-semibold text-acBlack mb-3">Submission & Follow Through</h3>
              <p className="text-gray-600">Form completion, appointment scheduling, and proactive status tracking</p>
            </div>
          </div>
        </div>
      </motion.section>

      {/* End-to-end process Section */}
      <motion.section className="py-16 px-6 bg-gray-50" {...fadeInUp}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack text-center mb-12">
            End-to-end process
          </h2>
          <div className="grid md:grid-cols-6 gap-8">
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-500 text-white rounded-full flex items-center justify-center mx-auto mb-4 font-bold text-lg">
                1
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-2">Intake and Viability Screen</h3>
              <p className="text-gray-600 text-sm">Review profile, nationality, funding path, and target destination</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-500 text-white rounded-full flex items-center justify-center mx-auto mb-4 font-bold text-lg">
                2
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-2">Strategy Call and Written Plan</h3>
              <p className="text-gray-600 text-sm">Confirm route, investment amount, ownership structure, and timeline</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-500 text-white rounded-full flex items-center justify-center mx-auto mb-4 font-bold text-lg">
                3
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-2">Business Plan and Evidence Build</h3>
              <p className="text-gray-600 text-sm">Produce plan, build market references, and start source of funds trail</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-500 text-white rounded-full flex items-center justify-center mx-auto mb-4 font-bold text-lg">
                4
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-2">Company and Banking Setup</h3>
              <p className="text-gray-600 text-sm">Coordinate formation, registrations, and bank onboarding where allowed</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-500 text-white rounded-full flex items-center justify-center mx-auto mb-4 font-bold text-lg">
                5
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-2">Submission and Appointments</h3>
              <p className="text-gray-600 text-sm">Complete application, pay fees, book appointments, and quality review</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-500 text-white rounded-full flex items-center justify-center mx-auto mb-4 font-bold text-lg">
                6
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-2">Decision and Operational Ramp</h3>
              <p className="text-gray-600 text-sm">Guide registrations, payroll, leases, and reporting obligations</p>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Timelines Section */}
      <motion.section className="py-16 px-6" {...fadeInUp}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack text-center mb-12">
            Timelines and service levels
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-sm border">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <FileText className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-xl font-inter font-semibold text-acBlack text-center mb-4">Business Plan Drafting</h3>
              <div className="text-center mb-6">
                <span className="text-3xl font-bold text-blue-600">2-4</span>
                <span className="text-gray-600 ml-2">weeks</span>
              </div>
              <p className="text-gray-600 text-sm text-center">Depending on complexity and data availability</p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-sm border border-blue-200">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <PieChart className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-xl font-inter font-semibold text-acBlack text-center mb-4">Source of Funds Documentation</h3>
              <div className="text-center mb-6">
                <span className="text-3xl font-bold text-blue-600">2-8</span>
                <span className="text-gray-600 ml-2">weeks</span>
              </div>
              <p className="text-gray-600 text-sm text-center">Depending on funding paths and jurisdictions</p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-sm border">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Globe className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-xl font-inter font-semibold text-acBlack text-center mb-4">Government Processing</h3>
              <div className="text-center mb-6">
                <span className="text-3xl font-bold text-blue-600">Varies</span>
                <span className="text-gray-600 ml-2">by country</span>
              </div>
              <p className="text-gray-600 text-sm text-center">Season and priority services affect timing</p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-sm border">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Clock className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-xl font-inter font-semibold text-acBlack text-center mb-4">Communication</h3>
              <div className="text-center mb-6">
                <span className="text-3xl font-bold text-blue-600">24-72</span>
                <span className="text-gray-600 ml-2">hours</span>
              </div>
              <p className="text-gray-600 text-sm text-center">Response time, faster during booking and setup</p>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Documents Checklist Section */}
      <motion.section className="py-16 px-6 bg-gray-50" {...fadeInUp}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack text-center mb-12">
            Documents checklist (typical)
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div>
              <h3 className="text-xl font-inter font-semibold text-acBlack mb-6">Identity and Background</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-600 text-sm">Passport, previous visas, and resumes for principal and co-founders</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-600 text-sm">Police certificates and medicals where required</span>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-inter font-semibold text-acBlack mb-6">Source of Funds and Wealth</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-600 text-sm">Bank statements covering path of funds from origin to present</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-600 text-sm">Proof of earnings: payslips, tax returns, audited accounts</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-600 text-sm">Contracts of sale and closing statements for asset disposals</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-600 text-sm">Gift deeds, loan agreements, inheritance records</span>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-inter font-semibold text-acBlack mb-6">Company and Investment</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-600 text-sm">Incorporation documents and operating agreements</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-600 text-sm">Share purchase agreements and valuation evidence</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-600 text-sm">Leases, supplier contracts, customer letters</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-600 text-sm">Business plan, forecasts, and endorsements</span>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-inter font-semibold text-acBlack mb-6">Additional Requirements</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-600 text-sm">Foreign exchange and remittance proofs</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-600 text-sm">Registers of shareholders and directors</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-600 text-sm">Staffing plan and any incubator letters</span>
                </li>
              </ul>
            </div>
          </div>
          <p className="text-center text-gray-600 mt-8">
            We tailor this list during strategy since acceptable formats and time frames differ by destination.
          </p>
        </div>
      </motion.section>

      {/* Dependents and Family Section */}
      <motion.section className="py-16 px-6" {...fadeInUp}>
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack mb-8">
            Dependents and family
          </h2>
          <div className="bg-blue-50 p-8 rounded-lg">
            <p className="text-lg text-gray-600 mb-6">
              Spouse or partner and dependent children are usually eligible to accompany the principal.
            </p>
            <div className="grid md:grid-cols-3 gap-6 text-left">
              <div>
                <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Family Planning</h3>
                <p className="text-gray-600 text-sm">We plan family applications alongside the principal investor or founder</p>
              </div>
              <div>
                <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Additional Funds</h3>
                <p className="text-gray-600 text-sm">Confirm additional funds if required for dependents</p>
              </div>
              <div>
                <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Sequenced Filings</h3>
                <p className="text-gray-600 text-sm">Sequence filings so decisions align for the whole family</p>
              </div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Fees Section */}
      <motion.section className="py-16 px-6 bg-gray-50" {...fadeInUp}>
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack mb-8">
            Fees and what is not included
          </h2>
          <div className="bg-white p-8 rounded-lg shadow-sm mb-8">
            <h3 className="text-xl font-inter font-semibold text-acBlack mb-4">Our Stage-Based Fees Include</h3>
            <div className="grid md:grid-cols-2 gap-6 text-left">
              <ul className="space-y-3">
                <li className="flex items-center">
                  <CheckCircle className="w-4 h-4 text-blue-500 mr-3" />
                  <span className="text-gray-600">Strategy and eligibility assessment</span>
                </li>
                <li className="flex items-center">
                  <CheckCircle className="w-4 h-4 text-blue-500 mr-3" />
                  <span className="text-gray-600">Business plan and evidence build</span>
                </li>
              </ul>
              <ul className="space-y-3">
                <li className="flex items-center">
                  <CheckCircle className="w-4 h-4 text-blue-500 mr-3" />
                  <span className="text-gray-600">Submission and follow through</span>
                </li>
                <li className="flex items-center">
                  <CheckCircle className="w-4 h-4 text-blue-500 mr-3" />
                  <span className="text-gray-600">Optional post-approval support</span>
                </li>
              </ul>
            </div>
          </div>
          <div className="bg-blue-50 p-6 rounded-lg">
            <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Not Included (Quoted Separately)</h3>
            <p className="text-gray-600 text-sm">Government fees, biometrics, medical exams, police certificates, certified translations, courier/shipping, accountant or valuation reports, bank charges, incubator or endorsement fees, and lawyer fees where counsel must be engaged.</p>
          </div>
        </div>
      </motion.section>

      {/* Risks Section */}
      <motion.section className="py-16 px-6" {...fadeInUp}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack text-center mb-12">
            Risks and how we reduce them
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-4">Insufficient Source of Funds Trail</h3>
              <p className="text-gray-600 mb-4">Incomplete documentation of fund origins and transfers.</p>
              <div className="bg-blue-50 p-4 rounded">
                <p className="text-sm text-gray-700"><strong>Our mitigation:</strong> We map each transfer end to end and request documents that prove origin, ownership, and lawful transfer.</p>
              </div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-4">Passive or Paper Investment</h3>
              <p className="text-gray-600 mb-4">Investment appears passive rather than active business management.</p>
              <div className="bg-blue-50 p-4 rounded">
                <p className="text-sm text-gray-700"><strong>Our mitigation:</strong> We align the plan to active management, staffing, and real operations rather than purely financial holding.</p>
              </div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-4">Valuation and Acquisition Gaps</h3>
              <p className="text-gray-600 mb-4">Questionable business valuations or acquisition terms.</p>
              <div className="bg-blue-50 p-4 rounded">
                <p className="text-sm text-gray-700"><strong>Our mitigation:</strong> We obtain third-party valuation support or comparable sales where it strengthens credibility.</p>
              </div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-4">Timing Conflicts</h3>
              <p className="text-gray-600 mb-4">Misaligned escrow, banking, and filing deadlines.</p>
              <div className="bg-blue-50 p-4 rounded">
                <p className="text-sm text-gray-700"><strong>Our mitigation:</strong> We plan escrow, bank onboarding, lease start dates, and filing windows to avoid gaps.</p>
              </div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-4">Nationality Restrictions</h3>
              <p className="text-gray-600 mb-4">Ineligibility for certain treaty investor routes.</p>
              <div className="bg-blue-50 p-4 rounded">
                <p className="text-sm text-gray-700"><strong>Our mitigation:</strong> We confirm eligibility early and propose alternatives if you are not eligible.</p>
              </div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-4">Crypto or High-Risk Assets</h3>
              <p className="text-gray-600 mb-4">Difficulty proving legitimacy of digital asset proceeds.</p>
              <div className="bg-blue-50 p-4 rounded">
                <p className="text-sm text-gray-700"><strong>Our mitigation:</strong> We create conservative audit trail with exchange statements and fiat exit records, or advise on alternative funding sources.</p>
              </div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* After Approval Section */}
      <motion.section className="py-16 px-6 bg-gray-50" {...fadeInUp}>
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack mb-8">
            After approval
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 text-left">
            <div>
              <div className="w-12 h-12 bg-blue-500 text-white rounded-full flex items-center justify-center mb-4 font-bold">
                1
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Maintain Investment Level</h3>
              <p className="text-gray-600 text-sm">Keep investment level, staffing, and business activity as required</p>
            </div>
            <div>
              <div className="w-12 h-12 bg-blue-500 text-white rounded-full flex items-center justify-center mb-4 font-bold">
                2
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Keep Records</h3>
              <p className="text-gray-600 text-sm">Maintain records for audits and renewals: payroll, tax filings, customer contracts</p>
            </div>
            <div>
              <div className="w-12 h-12 bg-blue-500 text-white rounded-full flex items-center justify-center mb-4 font-bold">
                3
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Track Renewal Dates</h3>
              <p className="text-gray-600 text-sm">Monitor renewal dates and thresholds for renewal or permanent residence</p>
            </div>
            <div>
              <div className="w-12 h-12 bg-blue-500 text-white rounded-full flex items-center justify-center mb-4 font-bold">
                4
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Plan Leadership Transitions</h3>
              <p className="text-gray-600 text-sm">Ensure share transfers or capital changes remain compliant</p>
            </div>
          </div>
        </div>
      </motion.section>

      {/* What We Need Section */}
      <motion.section className="py-16 px-6" {...fadeInUp}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack text-center mb-12">
            What we need to start
          </h2>
          <div className="bg-white p-8 rounded-lg shadow-sm">
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-inter font-semibold text-acBlack mb-4">Initial Information</h3>
                <ul className="space-y-3 text-gray-600">
                  <li>• Your nationality, target destination, and target start date</li>
                  <li>• Summary of your business plan or type of business you intend to start/acquire</li>
                  <li>• Breakdown of your funds and their origin, including supporting documents</li>
                  <li>• Names and roles of any co-founders or key hires</li>
                  <li>• Any endorsements, incubator interest, or letters of intent secured</li>
                  <li>• Immigration history including refusals and any criminal or regulatory issues</li>
                </ul>
              </div>
              <div>
                <h3 className="text-xl font-inter font-semibold text-acBlack mb-4">Next Steps</h3>
                <ul className="space-y-3 text-gray-600">
                  <li>• Schedule comprehensive viability assessment</li>
                  <li>• Receive detailed route analysis and investment requirements</li>
                  <li>• Begin business plan development</li>
                  <li>• Start source of funds documentation process</li>
                  <li>• Coordinate corporate formation and banking setup</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* FAQ Section */}
      <motion.section className="py-16 px-6 bg-gray-50" {...fadeInUp}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack text-center mb-12">
            Service specific FAQs
          </h2>
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Can funds be gifted or loaned?</h3>
              <p className="text-gray-600">Often yes if the route permits it and if the gift or loan is fully documented. We provide deed templates and collect the giver or lender's evidence of funds. We also confirm whether the funds must be in your personal account for a minimum period.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Can I invest through a company rather than personally?</h3>
              <p className="text-gray-600">Sometimes yes, but ownership and control rules must still be satisfied. We confirm whether the immigration authority requires personal investment, direct ownership, or ultimate beneficial ownership disclosure.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Do real estate investments qualify?</h3>
              <p className="text-gray-600">Many destinations exclude passive real estate. Some allow investment through a trading company that develops or operates property. We confirm the rules and structure the plan accordingly.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Can I use proceeds from crypto or other digital assets?</h3>
              <p className="text-gray-600">Possibly, if you can produce a clear audit trail to fiat currency. We gather exchange statements, wallet addresses, and bank proofs that match. Some destinations will not accept crypto proceeds, so we confirm this early.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Can I run the business remotely?</h3>
              <p className="text-gray-600">Most entrepreneur routes expect active, on-site management. Remote-only arrangements usually weaken credibility. We align your role and presence with what the authority expects.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">What happens if my application is refused?</h3>
              <p className="text-gray-600">We issue a written analysis, identify evidence gaps or legal issues, and advise on refile, administrative review, or appeal with licensed counsel where required. We only recommend routes that are credible on the facts.</p>
            </div>
          </div>
        </div>
      </motion.section>


    </div>
  );
};

export default InvestorEntrepreneurVisas;
